import { Crown } from "lucide-react";

/**
 * DarePass premium profile frame — used across leaderboards and room player lists.
 *
 *  – Rotating golden ring wraps the avatar (photo itself stays STATIC — never rotates)
 *  – Subtle golden crown pip at the corner (no yellow "DP" tag anymore)
 *  – Name renders with a soft golden shimmer via <ProName />, no yellow box tag
 */
export default function ProAvatarFrame({ picture, name, size = "sm", isPro = false }) {
  const dims = size === "lg" ? "w-10 h-10" : size === "md" ? "w-8 h-8" : "w-6 h-6";
  const pipBox = size === "lg" ? "w-4 h-4" : size === "md" ? "w-3.5 h-3.5" : "w-3 h-3";
  const pipIcon = size === "lg" ? 10 : size === "md" ? 8 : 7;

  const avatar = picture ? (
    <img
      src={picture}
      alt={name || ""}
      className={`${dims} object-cover rounded-full`}
      data-testid={isPro ? "pro-avatar" : "player-avatar"}
    />
  ) : (
    <div className={`${dims} rounded-full bg-[#EAEB18] flex items-center justify-center text-[9px] font-black text-black`}>
      {name?.[0]?.toUpperCase() || "?"}
    </div>
  );

  if (!isPro) {
    return <div className="shrink-0">{avatar}</div>;
  }

  return (
    <div className={`relative shrink-0 ${dims}`} data-testid="darepass-frame">
      {/* Rotating gold ring — sits BEHIND the photo, so the photo never rotates */}
      <div className="absolute inset-0 rounded-full darepass-ring" aria-hidden="true" />
      {/* Photo — static on top of ring, small inset creates the visible 2px gold ring */}
      <div className="absolute inset-[2px] rounded-full overflow-hidden bg-[#0B0C10]">
        {avatar}
      </div>
      {/* Subtle DarePass pip (crown in dark disk with golden border — no more yellow box) */}
      <div
        className={`absolute -bottom-0.5 -right-0.5 ${pipBox} rounded-full bg-[#0B0C10] flex items-center justify-center border border-[#FFD700] shadow-[0_0_5px_rgba(255,215,0,0.7)]`}
        aria-label="DarePass member"
      >
        <Crown size={pipIcon} strokeWidth={2.5} className="darepass-crown-icon" />
      </div>
    </div>
  );
}

/**
 * DarePass name — soft golden shimmer only. No "DP" yellow tag.
 */
export function ProName({ name, isPro, className = "" }) {
  if (!isPro) {
    return <span className={className}>{name}</span>;
  }
  return (
    <span className={`darepass-name inline-flex items-center ${className}`} data-testid="darepass-name">
      <span className="darepass-name-text">{name}</span>
    </span>
  );
}
